import Link from 'next/link';
import React, { CSSProperties, ReactNode } from 'react';
import { ASSET_PREFIX, colors, runningLocally } from 'utils/constants';

export const ColorStyle = {
  Primary: 'Primary',
  Outline: 'Outline',
  Gray: 'Gray',
  Iceland: 'Iceland',
  OutlineDark: 'OutlineDark',
} as const;

interface RoundedButtonProps {
  href?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
  className?: string;
  id?: string;
  disabled?: boolean;
  backgroundColorOverride?: string;
  colorOverride?: string;
  hoverColor?: string;
  hoverTextColor?: string;
  secondary?: boolean;
  colorStyle?: keyof typeof ColorStyle | undefined;
  linkIsExternal?: boolean;
  loading?: boolean;
  loadingColor?: 'WHITE' | 'BLACK';
  style?: CSSProperties;
  rebrandDm2025?: boolean;
  fullWidth?: boolean;
  mini?: boolean;
}

const RoundedButton = ({
  href,
  onClick,
  children,
  className,
  id,
  disabled,
  backgroundColorOverride,
  hoverColor,
  hoverTextColor,
  colorOverride,
  secondary = false,
  colorStyle = ColorStyle.Primary,
  linkIsExternal = false,
  loading = false,
  loadingColor = 'WHITE',
  style = {},
  rebrandDm2025,
  fullWidth,
  mini,
}: RoundedButtonProps) => {
  if (mini) {
    // Mini is only applicable for 2025 onwards
    rebrandDm2025 = true;
    if (loading && runningLocally) {
      // The RoundedButton component needs rewriting from scratch. All RoundedButtons have a min-width defined. This is
      // fine for certain scenarios. However, the mini button should not have a min-width, as it is meant to tightly
      // wrap its content in its padding. A side-effect of this is that we no longer have a width defined when the
      // button is put into "loading" state - the button's width shrinks because the text that appears in the button
      // is no longer visible.
      //
      // This component should be rewritten completely so that:
      // - All styles are defined in a .module.css, rather than a mix of CSS and CSS-in-JS
      // - When the loading spinner appears, the text remains in the button but is marked as invisible for aria and
      //   hidden by CSS
      // - It should use a button element if href is not provided, and fix all of the ugly default button styling
      // - The click event should still work when a href is present
      // - The button should always have padding and border, even if the border is the same colour as the background to
      //   simplify styling between the solid and outline variants of the button
      // - Ideally, the new button should not have a min nor max width. It should have a fillContainer or inline
      //   property to control how the buttons width is calculated
      // - When the button is placed in a flex-container it should still fill the height of the flex container, with the
      //   text and loading indicator centered correctly.
      // - Review uses of the button's children to see if it is ever anyting other than text and an image
      throw new Error('Mini button cannot be set to loading.');
    } else {
      loading = false;
    }
  }

  let cName = className ? `roundedButton ${className}` : 'roundedButton';
  if (disabled) cName += ' disabled';
  if (loading) cName += ' loading';
  if (rebrandDm2025) cName += ' rebrandDm2025';
  if (fullWidth) cName += ' fullWidth';
  if (mini) cName += ' mini';

  if (secondary) colorStyle = ColorStyle.Outline;
  if (colorStyle == ColorStyle.OutlineDark) {
    colorStyle = ColorStyle.Outline;
    colorOverride = '#4a4a4a';
    hoverColor = 'rgba(74, 74, 74, 0.25)';
  }
  let backgroundColor, hoverBackgroundColor;
  switch (colorStyle) {
    case ColorStyle.Primary:
      backgroundColor = backgroundColorOverride || colors.primaryCta;
      hoverBackgroundColor = hoverColor || colors.primaryCtaPressed;
      style.color = colorOverride;
      break;

    case ColorStyle.Outline:
      backgroundColor = backgroundColorOverride || 'clear';
      hoverBackgroundColor = hoverColor || '#FAB8A1';
      style.color = colorOverride || colors.primaryCta;
      style.border = `3px solid ${colorOverride || colors.primaryCta}`;
      if (rebrandDm2025) {
        if (mini) {
          style.padding = disabled ? undefined : '5px 13px';
        } else {
          style.padding = disabled ? '10px 25px' : '17px 22px';
        }
      } else {
        style.padding = disabled ? '10px 25px' : '7px 22px';
      }
      hoverTextColor = 'white';
      break;

    case ColorStyle.Iceland:
      backgroundColor = backgroundColorOverride || colors.icelandRaffle.primary;
      hoverBackgroundColor = hoverColor || colors.icelandRaffle.pressed;
      break;

    case ColorStyle.Gray:
    default:
      backgroundColor = backgroundColorOverride || colors.primaryBrandGray;
      hoverBackgroundColor = hoverColor || colors.primaryBrandGrayPressed;
  }

  return (
    <>
      {href ? (
        linkIsExternal ? (
          <a href={href} target="_blank" rel="noreferrer">
            <div className={cName} style={style} id={id}>
              {loading ? (
                loadingColor === 'BLACK' ? (
                  <img src={`${ASSET_PREFIX}/images/spinner-black.svg`} />
                ) : (
                  <img src={`${ASSET_PREFIX}/images/spinner-white.svg`} />
                )
              ) : (
                children
              )}
            </div>
          </a>
        ) : (
          <Link href={href}>
            <div className={cName} style={style} id={id}>
              {loading ? (
                loadingColor === 'BLACK' ? (
                  <img src={`${ASSET_PREFIX}/images/spinner-black.svg`} />
                ) : (
                  <img src={`${ASSET_PREFIX}/images/spinner-white.svg`} />
                )
              ) : (
                children
              )}
            </div>
          </Link>
        )
      ) : (
        <div className={cName} onClick={onClick} style={style} id={id}>
          {loading ? (
            loadingColor === 'BLACK' ? (
              <img
                src={`${ASSET_PREFIX}/images/spinner-black.svg`}
                style={{
                  width: 'auto',
                  height: '1em',
                  display: 'block',
                  margin: '2.5px auto',
                }}
              />
            ) : (
              <img
                src={`${ASSET_PREFIX}/images/spinner-white.svg`}
                style={{
                  width: 'auto',
                  height: '1em',
                  display: 'block',
                  margin: '2.5px auto',
                }}
              />
            )
          ) : (
            children
          )}
        </div>
      )}

      <style jsx>
        {`
          .roundedButton {
            display: inline-block;
            cursor: pointer;
            padding: 10px 25px;
            text-align: center;
            font-weight: bold;
            white-space: nowrap;
            color: white;
            border-radius: 20px;
            background-color: ${backgroundColor};
            transition: 0.2s;
            text-transform: uppercase;
          }

          .roundedButton:active,
          .roundedButton:hover {
            background-color: ${hoverBackgroundColor};
            color: ${hoverTextColor || 'white'};
          }

          .roundedButton.disabled {
            color: #aaa !important;
            background-color: lightgray;
            pointer-events: none;
            border: none !important;
          }

          .roundedButton.loading {
            pointer-events: none;
          }

          .roundedButton img {
            vertical-align: middle;
          }

          .rebrandDm2025 {
            border-radius: 100px;
            line-height: 100%;
            font-size: 16px;
            font-weight: 800;
            padding-top: 16px;
            padding-bottom: 16px;
            /* Min-width of 219px on mobile, but never break out of the container */
            min-width: min(219px, 100%);
          }
          @media (min-width: 768px) {
            .rebrandDm2025 {
              padding-top: 20px;
              padding-bottom: 20px;
              font-size: 20px;
              line-height: 25px;
              /* Min-width of 343px on desktop, but never break out of the container */
              min-width: min(343px, 100%);
            }
          }
          .rebrandDm2025.mini {
            min-width: 0;
            padding: 8px 16px;
          }
          @media (min-width: 768px) {
            .rebrandDm2025.mini {
              min-width: 0;
              padding: 8px 16px;
            }
          }

          .rebrandDm2025.loading {
          }

          .fullWidth {
            width: 100%;
          }
        `}
      </style>
    </>
  );
};

export default RoundedButton;
